import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { isEmpty } from 'lodash-es';
import { getNullTextOfFilter, unitConversion } from '@/pages/datalab/utils/utils';
import { LIGHT_COLOR_ARR } from '../_config/_constant';
import {
  IHorizontalBarTooltipProps,
  IHorizontalBarDataProps,
  IHorizontalBarDataResult,
  IHorizontalBarOptionsProps,
  IHorizontalBarSeries,
  ChartOption,
} from '../_config/_interface';
import type { TSeriesData } from './types';
import {
  getDatalabTooltipFormatter,
  getTooltipConfig,
} from './tooltip';

/**
 * 从 series 解析 dimensions：优先后端字段，否则按 name 拆解；空值走 getNullTextOfFilter。
 */
const resolveSeriesDimensions = (
  task: { dimensions?: string | null; name?: string; metric?: string; },
  metricCount: number,
): string => {
  // 后端显式下发 dimensions（含 null/''）时优先使用；缺字段再按 name 拆解
  if (task.dimensions !== undefined) {
    return getNullTextOfFilter(task.dimensions);
  }
  const seriesName = task.name || task.metric || '';
  if (metricCount <= 1) {
    return getNullTextOfFilter(seriesName);
  }
  const parts = seriesName.split(',');
  const dimsFromName = parts.length > 1 ? parts.slice(0, -1).join(',') : '';
  return getNullTextOfFilter(dimsFromName);
};

/**
 * 将 wire 标量 data 转为 TSeriesData（条形图不支持对比）。
 */
const normalizeHorizontalBarSeriesData = (params: {
  rawData: Array<string | number | TSeriesData>;
  metric: string;
  valueType: string;
  dimensions: string;
  isOthers: boolean;
  shouldConvertPercent: boolean;
  displayConfig?: Record<string, TSeriesData['display']>;
}): TSeriesData[] => {
  const {
    rawData,
    metric,
    valueType,
    dimensions,
    isOthers,
    shouldConvertPercent,
    displayConfig,
  } = params;

  return rawData.map((item: string | number | TSeriesData): TSeriesData => {
    // wire 可能仍是标量；已对象化则取 value
    const wireValue: string | number | null | undefined =
      typeof item === 'object' && item !== null
        ? item.value
        : item;
    const rawValue = wireValue;
    let value: string | number = wireValue ?? '';

    if (
      shouldConvertPercent
      && wireValue !== null
      && wireValue !== undefined
      && wireValue !== ''
    ) {
      value = Decimal.div(Number(wireValue) || 0, 100).toString();
    }

    const display: TSeriesData['display'] = displayConfig?.[metric] ?? {};

    return {
      value,
      rawValue,
      metric,
      metricName: metric?.replace(/(t[0-2]_)/, '') || '',
      dimensions,
      valueType,
      isPercent: valueType === 'percent',
      isDecimalPercent: shouldConvertPercent,
      isOthers,
      display,
      isCompare: false,
      controlName: '',
      dateText: '',
    };
  });
};

/**
 * 条形图 tooltip：复用折线/柱状 Grid formatter；硬编码 stackedDynamicState 以保留 Others 置底。
 * 保持对现有 { params, metricNames, isOnlyOneDimension, ... } 包装签名可编译（方案 1，不做额外 sort）。
 */
export const horizontalBarTooltipFormatter = (props: IHorizontalBarTooltipProps): string => {
  const { params, metricNames, isOnlyOneDimension } = props;
  const formatter = getDatalabTooltipFormatter({
    metricNames,
    isOnlyOneDimension,
    stackedDynamicState: true,
  });
  // params 已收窄为 ITooltipParam[]（series 点为 TSeriesData），无需断言
  return formatter(params);
};

/**
 * 横向条形图数据处理钩子函数
 * @param props - 包含图表数据的属性对象
 * @returns 返回处理后的图表相关数据，包括数据类型映射、系列配置、图例数据和Y轴数据
 */
export const useHorizontalBarData = (props: IHorizontalBarDataProps): IHorizontalBarDataResult => {
  const { chartData, metricIsAllPercent, metricNames, displayConfig } = props;
  const { valueTypeMap, series, legendData, yAxisData } = useMemo((): IHorizontalBarDataResult => {
    try {
      if (!isEmpty(chartData)) {
        const { yAxis, series: seriesData } = chartData?.[0] || {};
        let yAxisDataResult: string[] = [];
        if (Array.isArray(yAxis)) {
          yAxisDataResult = yAxis.map((x: string | number | null) => getNullTextOfFilter(x));
        }
        const legendDataResult = seriesData?.map(
          (task: { name: string; metric: string; }) => task?.name || task?.metric || '',
        ) ?? [];
        const nextValueTypeMap: Record<string, string> = {};
        const metricCount = metricNames?.length ?? 0;

        const seriesResult: IHorizontalBarSeries[] = seriesData?.map(
          (task: IHorizontalBarSeries): IHorizontalBarSeries => {
            const {
              name,
              metric: taskMetric,
              valueType: taskValueType,
              data: taskData,
              is_others: isOthersFlag,
            } = task ?? {};
            const seriesName = name || taskMetric || '';
            const metric = taskMetric || '';
            const valueType = taskValueType || '';
            const shouldConvertPercent = valueType === 'percent' && !metricIsAllPercent;
            const isOthers = Boolean(isOthersFlag) || seriesName === 'Others';
            const dimensions = resolveSeriesDimensions(task, metricCount);
            // chartData wire 侧 data 仍可能是标量数组，归一化前按联合类型处理
            const rawData: Array<string | number | TSeriesData> = Array.isArray(taskData)
              ? (taskData as Array<string | number | TSeriesData>)
              : [];

            nextValueTypeMap[seriesName] = shouldConvertPercent ? 'decimalPercent' : valueType;

            const data = normalizeHorizontalBarSeriesData({
              rawData,
              metric,
              valueType,
              dimensions,
              isOthers,
              shouldConvertPercent,
              displayConfig,
            });

            return {
              ...task,
              name: seriesName,
              metric,
              valueType,
              data,
              barMaxWidth: 80,
              barCategoryGap: '50%',
              type: 'bar',
            };
          },
        ) ?? [];

        return {
          valueTypeMap: nextValueTypeMap,
          series: seriesResult,
          legendData: legendDataResult,
          yAxisData: yAxisDataResult,
        };
      }
      return { valueTypeMap: {}, series: [], legendData: [], yAxisData: [] };
    } catch (e) {
      console.error('useHorizontalBarData 错误', e);
      return { valueTypeMap: {}, series: [], legendData: [], yAxisData: [] };
    }
  }, [chartData, metricIsAllPercent, metricNames, displayConfig]);

  return {
    valueTypeMap,
    series,
    legendData,
    yAxisData,
  };
};

/**
 * 水平条形图配置Hook
 * 用于生成水平条形图的ECharts配置选项
 * @param props - 包含图表配置的属性对象
 * @returns 水平条形图的ECharts配置选项
 */
export const useHorizontalBarOptions = (props: IHorizontalBarOptionsProps): ChartOption => {
  const {
    series,
    yAxisData,
    legendData,
    tooltipFormatter,
    setOtherConfigFunc,
    tooltipMinTopPx,
  } = props;

  const option = useMemo((): ChartOption => {
    const baseOption: ChartOption = {
      grid: {
        left: 10,
        top: 10,
        right: 30,
        bottom: 45,
        containLabel: true,
      },
      color: LIGHT_COLOR_ARR,
      tooltip: getTooltipConfig({
        tooltipMinTopPx,
        formatter: tooltipFormatter,
      }),
      yAxis: {
        type: 'category',
        inverse: true,
        triggerEvent: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#838697',
          fontSize: 10,
          overflow: 'truncate',
          width: 200,
        },
        data: yAxisData ?? [],
      },
      xAxis: [{
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisLabel: {
          color: '#838697',
          fontSize: 12,
          formatter(value: string | number) {
            return unitConversion(value, true);
          },
        },
      }],
      legend: {
        left: 'center',
        bottom: -6,
        type: 'scroll',
        icon: 'circle',
        inactiveBorderWidth: 0,
        itemHeight: 8,
        itemWidth: 8,
        itemGap: 20,
        pageIconColor: '#444444',
        pageIconInactiveColor: '#888888',
        pageIconSize: 12,
        pageButtonPosition: 'end',
        textStyle: {
          color: '#838697',
          fontSize: 12,
          lineHeight: 16,
          padding: [2, 0, 0, 0],
        },
        data: legendData ?? [],
        formatter: (value: string) => value?.replace?.(/\n|@$/g, '') || value,
      },
      series: series ?? [],
    };

    return setOtherConfigFunc ? setOtherConfigFunc(baseOption) : baseOption;
  }, [
    tooltipFormatter,
    yAxisData,
    legendData,
    series,
    setOtherConfigFunc,
    tooltipMinTopPx,
  ]);

  return option;
};
